// Translation type definitions

export type Language = 'he' | 'en';

// Runtime language record from the DB (supports any language code)
export interface SiteLanguage {
  code: string;
  nameEn: string;
  nameNative: string;
  isRtl: boolean;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

// Default fallback languages (used before API responds)
export const DEFAULT_SITE_LANGUAGES: SiteLanguage[] = [
  { code: 'en', nameEn: 'English', nameNative: 'English', isRtl: false, isDefault: true,  isActive: true, sortOrder: 0 },
  { code: 'he', nameEn: 'Hebrew',  nameNative: 'עברית',   isRtl: true,  isDefault: false, isActive: true, sortOrder: 1 },
];

// Language configuration - single source of truth
export interface LanguageConfig {
  code: Language;
  isRTL: boolean;
  name: string;
  nativeName: string;
}

export const LANGUAGE_CONFIG: Record<Language, LanguageConfig> = {
  en: { code: 'en', isRTL: false, name: 'English', nativeName: 'English' },
  he: { code: 'he', isRTL: true, name: 'Hebrew', nativeName: 'עברית' },
};

// Default language (no URL prefix)
export const DEFAULT_LANGUAGE: Language = 'en';

// Languages that get a URL prefix (all except default)
export const SUPPORTED_LANGUAGES: Language[] = (Object.keys(LANGUAGE_CONFIG) as Language[])
  .filter(lang => lang !== DEFAULT_LANGUAGE);

// All available languages
export const ALL_LANGUAGES: Language[] = Object.keys(LANGUAGE_CONFIG) as Language[];

export interface NavTranslations {
  services: string;
  articles: string;
  caseStudies: string;
  resources: string;
  process: string;
  about: string;
  contact: string;
  langToggle: string;
}

export interface HeroStats {
  projects: string;
  experience: string;
  success: string;
}

/**
 * A single system screenshot shown in the hero carousel.
 * Order must match `SYSTEM_SLIDES` in `components/Hero.tsx`.
 */
export interface HeroSystemSlide {
  /** Short label under the frame: system name + what the AI does there. */
  caption: string;
  /** Descriptive alt text for the screenshot. */
  alt: string;
}

export interface HeroTranslations {
  badge: string;
  headline: string;
  subheadline: string;
  ctaPrimary: string;
  ctaSecondary: string;
  stats: HeroStats;
  scrollDown: string;
  scrollDownAria: string;
  systems: HeroSystemSlide[];
  systemsAria: string;
}

export interface ServiceItem {
  title: string;
  desc: string;
  cta: string;
  benefits: string[];
}

export interface ServicesTranslations {
  title: string;
  subtitle: string;
  crossCutting: string;
  aiAgents: ServiceItem;
  custom: ServiceItem;
  integration: ServiceItem;
  benefitsTitle: string;
  badge: string;
  learnMore: string;
  notSure: string;
  talkToUs: string;
}

export interface AboutValue {
  title: string;
  desc: string;
}

export interface AboutStats {
  yearsExp: string;
  projects: string;
  clients: string;
  success: string;
}

export interface AboutTranslations {
  title: string;
  subtitle: string;
  storyTitle: string;
  story: string[];
  teamTitle: string;
  team: string;
  serviceTitle: string;
  service: string;
  valuesTitle: string;
  valuesDesc: string;
  values: AboutValue[];
  cta: string;
  badge: string;
  whoWeAre: string;
  teamImageAlt: string;
  yearsExperience: string;
  techSolutionsAlt: string;
  whatWeDo: string;
  ourPrinciples: string;
  bookConsultation: string;
  ctaTitle: string;
  ctaDesc: string;
  stats: AboutStats;
}

export interface ServiceDetailFeature {
  title: string;
  desc: string;
}

export interface ServiceDetailUseCase {
  title: string;
  desc: string;
}

export interface ServiceDetailItem {
  title: string;
  subtitle: string;
  paragraphs: string[];
  features: ServiceDetailFeature[];
  stack: string[];
  useCases?: ServiceDetailUseCase[];
  benefits?: string[];
  caseStudyHint?: string;
  integrations?: string[];
}

export interface ServiceDetailCommon {
  backToHome: string;
  professionalOverview: string;
  techCapabilities: string;
  readyToScale: string;
  readyToScaleDesc: string;
  bookConsultation: string;
  whyChooseUs: string;
  experienceTopBrands: string;
  longTermSupport: string;
  viewCaseStudies: string;
  successStoriesTitle: string;
  successStoriesDesc: string;
}

export interface ServiceDetailTranslations {
  'ai-agents': ServiceDetailItem;
  integration: ServiceDetailItem;
  'custom-software': ServiceDetailItem;
  common: ServiceDetailCommon;
}

export interface ProcessStep {
  title: string;
  desc: string;
  deliverables: string[];
  clientAction: string;
}

export interface ProcessStats {
  projects: string;
  satisfaction: string;
  years: string;
  response: string;
}

export interface ProcessTranslations {
  title: string;
  subtitle: string;
  steps: ProcessStep[];
  cta: string;
  ctaSubtext: string;
  badge: string;
  details: string;
  whatYouGet: string;
  yourPart: string;
  ctaButton: string;
  stats: ProcessStats;
}

export interface DifferentiatorItem {
  title: string;
  desc: string;
}

export interface WhyUsTranslations {
  badge: string;
  title: string;
  desc: string;
  differentiators: DifferentiatorItem[];
  founderName: string;
  founderTitle: string;
  founderQuote: string;
  cta: string;
  commitmentTitle: string;
  commitmentDesc: string;
  uptime: string;
}

export interface ContactFormValidation {
  nameRequired: string;
  nameMinLength: string;
  nameMaxLength: string;
  emailRequired: string;
  emailInvalid: string;
  phoneRequired: string;
  phoneInvalid: string;
  companyMaxLength: string;
  interestRequired: string;
  messageMaxLength: string;
}

export interface ContactFormTranslations {
  name: string;
  email: string;
  phone: string;
  company: string;
  interest: string;
  message: string;
  submit: string;
  options: string[];
  successTitle: string;
  successDesc: string;
  validation: ContactFormValidation;
}

export interface ContactRecommendation {
  quote: string;
  author: string;
  position: string;
}

export interface ContactTranslations {
  title: string;
  subtitle: string;
  callUs: string;
  emailUs: string;
  form: ContactFormTranslations;
  recommendation: ContactRecommendation;
}

export interface FooterTranslations {
  desc: string;
  copyright: string;
  // Section headers
  servicesTitle: string;
  companyTitle: string;
  contactTitle: string;
  // Service links
  aiAgents: string;
  customSoftware: string;
  systemIntegration: string;
  // Company links
  about: string;
  articles: string;
  caseStudies: string;
  resources: string;
  contact: string;
  // Legal links
  privacy: string;
  terms: string;
  accessibility: string;
  // New elements
  location: string;
  whatsappChat: string;
  ctaTitle: string;
  ctaButton: string;
  // Social media aria-labels
  followLinkedin: string;
  followFacebook: string;
  followInstagram: string;
  comingSoon: string;
  socialLinkComingSoon: string;
}

// Accessibility Widget translations
export interface AccessibilityTranslations {
  title: string;
  close: string;
  reset: string;
  fontSize: string;
  increase: string;
  decrease: string;
  highContrast: string;
  highlightLinks: string;
  readableFont: string;
  pauseAnimations: string;
  statement: string;
  buttonLabel: string;
}

export interface AITranslations {
  welcome: string;
  placeholder: string;
  online: string;
}

export interface LogoMarqueeTranslations {
  badge: string;
  titlePart1: string;
  titleHighlight: string;
  subtitle: string;
  ctaQuestion: string;
  ctaLink: string;
}

export interface TestimonialItem {
  quote: string;
  name: string;
  title: string;
  company: string;
  image: string;
}

export interface TestimonialsTranslations {
  title: string;
  subtitle: string;
  items: TestimonialItem[];
}

export interface LogoBarTranslations {
  title: string;
}

export interface ProblemItem {
  title: string;
  desc: string;
}

export interface ProblemsTranslations {
  badge: string;
  title: string;
  subtitle: string;
  problems: ProblemItem[];
  cta: string;
}

export interface NotFoundTranslations {
  errorCode: string;
  title: string;
  subtitle: string;
  description: string;
  backHome: string;
  contact: string;
  popularPages: string;
  services: string;
  about: string;
  aiAgents: string;
  integration: string;
  customDev: string;
  searchHint: string;
}

export interface ArticlesPageTranslations {
  badge: string;
  title: string;
  subtitle: string;
  readMore: string;
  noArticles: string;
  ctaText: string;
  ctaButton: string;
}

export interface SeoRouteTranslations {
  title: string;
  description: string;
}

export interface SeoTranslations {
  siteName: string;
  home: SeoRouteTranslations;
  about: SeoRouteTranslations;
  aiAgents: SeoRouteTranslations;
  integrationServices: SeoRouteTranslations;
  customDevelopment: SeoRouteTranslations;
  contact: SeoRouteTranslations;
  caseStudies: SeoRouteTranslations;
  articles: SeoRouteTranslations;
  resources: SeoRouteTranslations;
  privacy: SeoRouteTranslations;
  terms: SeoRouteTranslations;
  accessibility: SeoRouteTranslations;
  caseStudyDetail: SeoRouteTranslations;
  articleDetail: SeoRouteTranslations;
  resourceDetail: SeoRouteTranslations;
  notFoundPage: SeoRouteTranslations;
}

export interface ContentDraftListTranslations {
  title: string;
  runPipeline: string;
  runPipelineDryRun: string;
  emptyState: string;
  tabs: { all: string; pending: string; approved: string; published: string; rejected: string; failed: string };
  columns: { title: string; topic: string; generated: string; status: string; actions: string };
  status: { pending: string; approved: string; published: string; rejected: string; failed: string };
  actions: { review: string; reject: string };
}

export interface ContentDraftReviewTranslations {
  backToList: string;
  tabs: { enArticle: string; heArticle: string; linkedinPost: string; seoMeta: string; cardPreview: string };
  actions: { saveEdits: string; regenerate: string; reject: string; approveWebsiteOnly: string; approveAndPublish: string };
  publishTarget: { label: string; articles: string; resources: string };
  rejectModal: { title: string; placeholder: string; confirm: string; cancel: string };
  seoScore: { title: string; wordCount: string; keywordInH1: string; keywordInFirst100: string; metaLength: string; internalLinks: string };
  metadata: { topic: string; keywords: string; sources: string; estimatedCost: string; generatedAt: string };
  autoSaving: string;
  saved: string;
  charCount: string;
  linkedinPanel: {
    title: string;
    notScheduled: string;
    scheduleBtn: string;
    postNowBtn: string;
    changeBtn: string;
    cancelBtn: string;
    scheduledFor: string;
    publishedAt: string;
    failedWith: string;
    confirmTitle: string;
    confirmSave: string;
    confirmCancel: string;
  };
}

export interface LinkedInScheduleTranslations {
  title: string;
  connection: { connected: string; partial: string; notConnected: string; connectBtn: string };
  scheduleNewBtn: string;
  monthly: string;
  weekly: string;
  status: { not_scheduled: string; scheduled: string; published: string; failed: string; pending: string };
  modal: { title: string; selectDraft: string; dateTime: string; save: string; cancel: string; noDrafts: string };
  editModal: { title: string; newTime: string; save: string; cancelPost: string; close: string };
  postNow: string;
  scheduledFor: string;
  publishedAt: string;
  noPostsInPeriod: string;
  daysShort: string[];
  months: string[];
}

export interface ContentTopicFormTranslations {
  titleEn: string;
  keywordsEn: string;
  translations: string;
  translationsAdd: string;
  translationsLang: string;
  translationsTitle: string;
  translationsKeywords: string;
  translationsRemove: string;
  angle: string;
  audience: string;
  priority: string;
  notes: string;
  isRecurring: string;
  status: string;
  save: string;
  cancel: string;
}

export interface ContentTopicTranslations {
  title: string;
  createNew: string;
  emptyState: string;
  columns: { title: string; angle: string; priority: string; status: string; actions: string };
  status: { active: string; inProgress: string; done: string; paused: string };
  angles: { educational: string; caseStudy: string; thoughtLeadership: string; howTo: string; newsReaction: string };
  languageNames: Record<string, string>;
  form: ContentTopicFormTranslations;
  actions: { edit: string; delete: string };
  deleteConfirm: string;
}

export interface AdminHelpTipsTranslations {
  topics: {
    page: string;
    angle: string;
    priority: string;
    status: string;
    isRecurring: string;
    keywordsEn: string;
    translations: string;
    audience: string;
    notes: string;
  };
  drafts: {
    page: string;
    runPipeline: string;
    dryRun: string;
  };
  draftReview: {
    regenerate: string;
    reject: string;
    tabEn: string;
    tabHe: string;
    tabLinkedin: string;
    tabSeoMeta: string;
    tabCardPreview: string;
    seoScore: string;
  };
}

export interface AdminSettingsTranslations {
  title: string;
  languages: {
    title: string;
    description: string;
    code: string;
    nameEn: string;
    nameNative: string;
    isRtl: string;
    isActive: string;
    defaultBadge: string;
    addTitle: string;
    codePlaceholder: string;
    nameEnPlaceholder: string;
    nameNativePlaceholder: string;
    save: string;
    cancel: string;
    deleteConfirm: string;
    cannotDeleteDefault: string;
  };
}

export interface SeoIntelligenceTranslations {
  title: string;
  refresh: string;
  generating: string;
  lastRefresh: string;
  never: string;
  days7: string;
  days28: string;
  days90: string;
  keywords: {
    title: string;
    query: string;
    page: string;
    clicks: string;
    impressions: string;
    ctr: string;
    position: string;
    createArticle: string;
  };
  pages: {
    title: string;
    page: string;
    sessions: string;
    bounceRate: string;
    avgDuration: string;
    declining: string;
  };
  gaps: {
    title: string;
    description: string;
    empty: string;
  };
  recommendations: {
    title: string;
    generate: string;
    generatedAt: string;
    addToTopics: string;
    empty: string;
    priority: { high: string; medium: string; low: string };
  };
  errors: {
    keywordsFailed: string;
    pagesFailed: string;
    notConfigured: string;
  };
}

export interface PromptsManagerTranslations {
  title: string;
  intro: { title: string; description: string };
  empty: string;
  notSet: string;
  lastUpdated: string;
  chars: string;
  placeholder: string;
  confirmDelete: string;
  direction: { label: string; ltr: string; rtl: string };
  badges: { custom: string; default: string };
  actions: { save: string; reset: string; delete: string };
  toasts: { saved: string; deleted: string };
  errors: { loadFailed: string; saveFailed: string; deleteFailed: string; emptyPrompt: string };
}

export interface AdminContentTranslations {
  contentDrafts: ContentDraftListTranslations;
  draftReview: ContentDraftReviewTranslations;
  contentTopics: ContentTopicTranslations;
  nav: { drafts: string; topics: string; pipelineRunning: string; pipelineSuccess: string; pipelineError: string; dismiss: string };
  helpTips: AdminHelpTipsTranslations;
  settings: AdminSettingsTranslations;
  linkedinSchedule: LinkedInScheduleTranslations;
  seoIntelligence: SeoIntelligenceTranslations;
  prompts: PromptsManagerTranslations;
}

export interface LanguageTranslations {
  nav: NavTranslations;
  hero: HeroTranslations;
  problems: ProblemsTranslations;
  services: ServicesTranslations;
  about: AboutTranslations;
  serviceDetail: ServiceDetailTranslations;
  process: ProcessTranslations;
  whyUs: WhyUsTranslations;
  contact: ContactTranslations;
  footer: FooterTranslations;
  ai: AITranslations;
  logoMarquee: LogoMarqueeTranslations;
  notFound: NotFoundTranslations;
  testimonials: TestimonialsTranslations;
  logoBar: LogoBarTranslations;
  accessibility: AccessibilityTranslations;
  articlesPage: ArticlesPageTranslations;
  seo: SeoTranslations;
  adminContent: AdminContentTranslations;
}

export interface Translations {
  he: LanguageTranslations;
  en: LanguageTranslations;
}

